import React, { useEffect, useState } from 'react';
import {
  Container,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Paper,
  Divider,
  Chip,
  Box,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { Edit, Visibility, Search, FilterList } from '@mui/icons-material';
import ViewUpdateItemDetails from './view-update-details';
import BackButton from '../BackButton';
import axiosAPIinstance from '../../config/axiosAPIinstance';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ViewAllItems = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedRecommend, setSelectedRecommend] = useState('');
  const [purchaseDateRange, setPurchaseDateRange] = useState([null, null]);
  const [expiryDateRange, setExpiryDateRange] = useState([null, null]);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    getProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [items, searchTerm, selectedBrand, selectedCategory, selectedTag, selectedRecommend, purchaseDateRange, expiryDateRange]);

  const getProducts = () => {
    setLoading(true);
    axiosAPIinstance.get('/product/getProductsList')
      .then(response => {
        setItems(response.data);
        setFilteredItems(response.data);
      })
      .catch(error => console.error('Error fetching items:', error))
      .finally(() => {
        setLoading(false); // Set loading to false after the request completes
      });
  }

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setIsEditMode(false);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setIsEditMode(true);
  };

  const handleCloseDetails = () => {
    setSelectedItem(null);
  };

  const applyFilters = () => {
    let result = [...items];

    // Text search across multiple fields
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.productName?.toLowerCase().includes(term) ||
        item.hsCodeBarCode?.toLowerCase().includes(term) ||
        item.brand?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term) ||
        item.tags?.toLowerCase().includes(term) ||
        item.recommend?.toLowerCase().includes(term)
      );
    }

    // Brand filter
    if (selectedBrand) {
      result = result.filter(item => item.brand === selectedBrand);
    }

    // Category filter
    if (selectedCategory) {
      result = result.filter(item => item.category === selectedCategory);
    }

    // Tag filter
    if (selectedTag) {
      result = result.filter(item => item.tags?.includes(selectedTag));
    }

    // Recommend filter
    if (selectedRecommend) {
      result = result.filter(item => item.recommend === selectedRecommend);
    }

    // Purchase date range filter
    if (purchaseDateRange[0] || purchaseDateRange[1]) {
      result = result.filter(item => {
        if (!item.purchaseDate) return false;

        const purchaseDate = new Date(item.purchaseDate);
        const fromDate = purchaseDateRange[0] ? new Date(purchaseDateRange[0]) : null;
        const toDate = purchaseDateRange[1] ? new Date(purchaseDateRange[1]) : null;

        return (!fromDate || purchaseDate >= fromDate) &&
          (!toDate || purchaseDate <= toDate);
      });
    }

    // Expiry date range filter
    if (expiryDateRange[0] || expiryDateRange[1]) {
      result = result.filter(item => {
        if (!item.expiryDate) return false;

        const expiryDate = new Date(item.expiryDate);
        const fromDate = expiryDateRange[0] ? new Date(expiryDateRange[0]) : null;
        const toDate = expiryDateRange[1] ? new Date(expiryDateRange[1]) : null;

        return (!fromDate || expiryDate >= fromDate) &&
          (!toDate || expiryDate <= toDate);
      });
    }

    setFilteredItems(result);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedBrand('');
    setSelectedCategory('');
    setSelectedTag('');
    setSelectedRecommend('');
    setPurchaseDateRange([null, null]);
    setExpiryDateRange([null, null]);
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (property) => {
    const values = items.map(item => item[property]).filter(Boolean);
    return [...new Set(values)];
  };

  // Extract all tags from all items
  const getAllTags = () => {
    const allTags = items
      .map(item => item.tags?.split(',').map(tag => tag.trim()))
      .flat()
      .filter(Boolean);
    return [...new Set(allTags)];
  };

  return (
    <div style={{ margin: '3rem' }}>
      <Container>
        <Typography variant="h4" gutterBottom>
          Products List
        </Typography>

        {/* Search and Filter Section */}
        <Paper elevation={3} style={{ padding: '1rem', marginBottom: '2rem' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} style={{ textAlign: 'right' }}>
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                color={showFilters ? 'primary' : 'default'}
              >
                <FilterList />
                <Typography variant="body2" style={{ marginLeft: '0.5rem' }}>
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Typography>
              </IconButton>
            </Grid>
          </Grid>

          {showFilters && (
            <>
              <Divider style={{ margin: '1rem 0' }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <Typography variant="subtitle2" gutterBottom>Brand</Typography>
                    <Select
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {getUniqueValues('brand').map(brand => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <Typography variant="subtitle2" gutterBottom>Category</Typography>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {getUniqueValues('category').map(category => (
                        <MenuItem key={category} value={category}>{category}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <Typography variant="subtitle2" gutterBottom>Tags</Typography>
                    <Select
                      value={selectedTag}
                      onChange={(e) => setSelectedTag(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">All Tags</MenuItem>
                      {getAllTags().map(tag => (
                        <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <Typography variant="subtitle2" gutterBottom>Recommendation</Typography>
                    <Select
                      value={selectedRecommend}
                      onChange={(e) => setSelectedRecommend(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">All</MenuItem>
                      {getUniqueValues('recommend').map(recommend => (
                        <MenuItem key={recommend} value={recommend}>{recommend}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Typography variant="subtitle2" gutterBottom>Purchase Date Range</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="From"
                          value={purchaseDateRange[0]}
                          onChange={(newValue) => setPurchaseDateRange([newValue, purchaseDateRange[1]])}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label="To"
                          value={purchaseDateRange[1]}
                          onChange={(newValue) => setPurchaseDateRange([purchaseDateRange[0], newValue])}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Typography variant="subtitle2" gutterBottom>Expiry Date Range</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="From"
                          value={expiryDateRange[0]}
                          onChange={(newValue) => setExpiryDateRange([newValue, expiryDateRange[1]])}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label="To"
                          value={expiryDateRange[1]}
                          onChange={(newValue) => setExpiryDateRange([expiryDateRange[0], newValue])}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                  <Chip
                    label="Reset Filters"
                    onClick={resetFilters}
                    variant="outlined"
                    style={{ marginRight: '1rem' }}
                  />
                  <Chip
                    label={`Showing ${filteredItems.length} of ${items.length} products`}
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Paper>

        {/* Product List */}
        <List>
          {loading ? (
            <Backdrop
              open={loading}
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
              <CircularProgress color="inherit" size={60} thickness={5} />
            </Backdrop>
          ) :
            filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <ListItem key={item.itemId} divider>
                  <ListItemAvatar>
                    <Avatar src={item.image} alt={item.productName} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.productName}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" display="block">
                          {item.brand} • {item.category}
                        </Typography>
                        <Typography component="span" variant="body2" display="block">
                          {item.hsCodeBarCode}
                        </Typography>
                      </>
                    }
                  />
                  <IconButton onClick={() => handleViewDetails(item)}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleEditItem(item)}>
                    <Edit />
                  </IconButton>
                </ListItem>
              ))
            ) : (
              <Typography variant="body1" style={{ textAlign: 'center', margin: '2rem' }}>
                No products found matching your criteria
              </Typography>
            )}
        </List>

        {selectedItem && (
          <ViewUpdateItemDetails
            item={selectedItem}
            isEditMode={isEditMode}
            onClose={handleCloseDetails}
            onSave={() => {
              getProducts();
              handleCloseDetails();
            }}
          />
        )}
      </Container>

      <div className="indent-container">
        <BackButton />
      </div>
    </div>
  );
};

export default ViewAllItems;